import { useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
  type ComboxContextProps,
  ComboxProvider,
  useComboxContext,
} from "~/components/shared/Combox/ComboxContext";
import ComboxTrigger from "~/components/shared/Combox/ComboxTrigger";
import ComboxPopover, { type ComboxPopoverProps } from "~/components/shared/Combox/ComboxPopover";
import ComboxFilterInput from "~/components/shared/Combox/ComboxFilterInput";
import ComboxList, { type ComboxListProps } from "~/components/shared/Combox/ComboxList";
import ComboxItem from "~/components/shared/Combox/ComboxItem";
import ComboxListGroup from "~/components/shared/Combox/ComboxListGroup";
import Icon from "~/components/shared/Icon";
import Emojify from "~/components/shared/Emojify";

export interface ComboxItemData {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: string;
}

interface ComboxGroupData {
  label: string;
  items: ComboxItemData[];
}

export interface ComboxProps extends Omit<ComboxContextProps, "children"> {
  children?: React.ReactNode;
  items?: ComboxItemData[];
  // A superset of `items` used to resolve selected labels and Rails hidden
  // inputs when the visible list is filtered down to a subset.
  knownItems?: ComboxItemData[];
  groups?: ComboxGroupData[];
  triggerProps?: {
    placeholder?: string;
    disabled?: boolean;
    clearable?: boolean;
    leadingIcon?: string;
    className?: string;
    subtle: boolean;
  };
  filterProps?: {
    placeholder?: string;
  };
  loading?: boolean;
  empty_state?: string;
  filterItems?: boolean;
  hiddenInputName?: string;
  popoverProps?: Omit<ComboxPopoverProps, "children">;
  listProps?: Omit<ComboxListProps, "children">;
}

// For use with Rails form handlers
const ComboxHiddenInputs = ({ items, name }: { items: ComboxItemData[]; name: string }) => {
  const { selectedValue, selectedValues, multiple } = useComboxContext();
  return (
    <>
      {multiple && <input type="hidden" value="" name={name} readOnly={true} />}
      {items.map((item) => {
        const isSelected = multiple
          ? selectedValues.includes(item.value)
          : selectedValue === item.value;
        return (
          <input
            type={multiple ? "checkbox" : "radio"}
            value={item.value === "nil" ? "" : item.value} // Convert "nil" string to empty value for rails params
            id={item.value}
            checked={isSelected}
            name={name}
            hidden={true}
            readOnly={true}
            key={item.value}
          />
        );
      })}
    </>
  );
};

export const Combox = ({
  children,
  open,
  onOpenChange,
  items,
  knownItems,
  groups,
  triggerProps,
  filterProps,
  loading = false,
  empty_state,
  filterItems = true,
  multiple = false,
  hiddenInputName,
  popoverProps,
  listProps,
  onValueChange,
  onValuesChange,
  ...props
}: ComboxProps) => {
  const [internalOpen, setInternalOpen] = useState(false);

  const shouldUseInternalState = (items && items.length > 0) || (groups && groups.length > 0);
  const renderHiddenInputs = !!hiddenInputName;
  const shouldManageOpenStateInternally = shouldUseInternalState || renderHiddenInputs;
  const currentOpen = shouldManageOpenStateInternally ? internalOpen : open;
  const currentOnOpenChange = shouldManageOpenStateInternally ? setInternalOpen : onOpenChange;

  const handleValueChange = (value: string) => {
    onValueChange?.(value);

    if (shouldUseInternalState && !multiple) {
      setInternalOpen(false);
    }
  };

  const handleValuesChange = (values: string[]) => {
    onValuesChange?.(values);
  };

  if (shouldUseInternalState || renderHiddenInputs) {
    const resolvedKnownItems = groups
      ? groups.flatMap((group) => group.items)
      : knownItems || items || [];

    // Build item labels map for trigger (includes icons)
    const itemLabels: Record<string, React.ReactNode> = {};
    resolvedKnownItems.forEach((item) => {
      itemLabels[item.value] = (
        <div className="flex items-center">
          {item.icon && <Icon icon={item.icon} className="mr-2 h-4 w-4" />}
          <Emojify text={item.label} />
        </div>
      );
    });

    return (
      <PopoverPrimitive.Root open={currentOpen} onOpenChange={currentOnOpenChange}>
        <ComboxProvider
          open={currentOpen}
          onOpenChange={currentOnOpenChange}
          multiple={multiple}
          onValueChange={handleValueChange}
          onValuesChange={handleValuesChange}
          {...props}
        >
          <ComboxTrigger {...(triggerProps || {})} itemLabels={itemLabels} />
          <ComboxPopover {...popoverProps}>
            <ComboxFilterInput {...(filterProps || {})} />
            <ComboxList
              loading={loading}
              emptyState={empty_state || "No results found"}
              filterItems={filterItems}
              {...listProps}
            >
              {children
                ? children
                : groups && groups.length > 0
                  ? groups.map((group) => (
                      <ComboxListGroup key={group.label} label={group.label}>
                        {group.items.map((item) => (
                          <ComboxItem key={item.value} value={item.value} disabled={item.disabled}>
                            <div className="flex items-center">
                              {item.icon && <Icon icon={item.icon} className="mr-2 h-4 w-4" />}
                              {item.label}
                            </div>
                          </ComboxItem>
                        ))}
                      </ComboxListGroup>
                    ))
                  : (items || []).map((item) => (
                      <ComboxItem key={item.value} value={item.value} disabled={item.disabled}>
                        <div className="flex items-center">
                          {item.icon && <Icon icon={item.icon} className="mr-2 h-4 w-4" />}
                          {item.label}
                        </div>
                      </ComboxItem>
                    ))}
            </ComboxList>
          </ComboxPopover>
          {renderHiddenInputs && (
            <ComboxHiddenInputs items={resolvedKnownItems} name={hiddenInputName} />
          )}
        </ComboxProvider>
      </PopoverPrimitive.Root>
    );
  }
  return (
    <PopoverPrimitive.Root open={currentOpen} onOpenChange={currentOnOpenChange}>
      <ComboxProvider
        open={currentOpen}
        onOpenChange={currentOnOpenChange}
        multiple={multiple}
        onValueChange={onValueChange}
        onValuesChange={onValuesChange}
        {...props}
      >
        {children}
      </ComboxProvider>
    </PopoverPrimitive.Root>
  );
};

export default Combox;
